

































import { mapGetters } from 'vuex'
import SchemeCommon from '@/abstract/SchemeCommon'
import Search from '@/components/Search/Search.vue'
import TabsPanelWithSmartScroll from '@/components/Common/TabsPanelWithSmartScroll.vue'
import SchemeCard from '@/components/SchemeCard/SchemeCard.vue'
import CardOptionBadge from '@/components/CardOptionBadge/CardOptionBadge.vue'

import type { ScriptUnit, SchemeCardInfo, CardOption } from '@/models/scripts'
import type { Scheme } from '@/models/scheme'
import { CardOptionType } from '@/constants/scripts'

interface Data {
  zoom: number
  searchValue: string
  searchIsOpen: boolean
  searchIsFocus: boolean
}

interface Methods {
  handleChangeZoom: (zoom: number) => void
  handleSetSearchOpen: (isOpen: boolean) => void
  handleSearchFocus: () => void
  handleSearchBlur: () => void
}

interface Computed {
  root?: SchemeCardInfo
  scheme: Scheme
  getSchemeUpdate: number
  schemeIsBackgrounded: boolean
  schemeStyles: Partial<CSSStyleDeclaration>
  searchListStyle: Partial<CSSStyleDeclaration>
  shownCards: SchemeCardInfo[]
  filteredCards: SchemeCardInfo[]
}

interface Props {
  scriptItem: ScriptUnit
}

export default SchemeCommon.extend<Data, Methods, Computed, Props>({
  components: {
    Search,
    SchemeCard,
    CardOptionBadge,
    TabsPanelWithSmartScroll,
  },
  props: {
    scriptItem: {
      required: true,
    },
  },
  data() {
    return {
      zoom: 1,
      searchValue: '',
      searchIsOpen: false,
      searchIsFocus: false,
    }
  },
  methods: {
    handleChangeZoom(zoom: number): void {
      this.zoom = zoom
    },
    handleSetSearchOpen(isOpen: boolean): void {
      if (!isOpen && this.searchIsFocus) {
        (this.$refs.popoverElement as any).show()
      }
      this.searchIsOpen = this.searchIsFocus || isOpen
    },
    handleSearchFocus(): void {
      this.searchIsFocus = true
    },
    handleSearchBlur(): void {
      this.searchIsFocus = false
    }
  },
  computed: {
    ...mapGetters({
      root: 'getSchemeRoot',
      scheme: 'getScriptScheme',
      getSchemeUpdate: 'getSchemeUpdate',
      schemeIsBackgrounded: 'getSchemeIsBackgrounded',
    }),
    schemeStyles(): Record<string, string> {
      return {
        gridTemplateColumns: `repeat(${this.scheme.depth}, 28rem)`,
      }
    },
    searchListStyle(): Partial<CSSStyleDeclaration> {
      const { $el } = (this.$refs.popoverElement as Vue) || {}
      return {
        width: !this.searchIsOpen && !$el ? 'auto' : `${$el.clientWidth}px`,
      }
    },
    shownCards(): SchemeCardInfo[] {
      const nodes = []
      const queue = [this.root] // cards from tree by it's deep
      const visited: Set<number> = new Set()
      while (queue.length > 0) {
        const card = queue.shift()
        if (card === undefined) {
          throw `Something went wrong in SchemeGenerator`
        }
        if (!visited.has(card.num)) {
          nodes.push(card)
        }
        visited.add(card.num)
        const content = card.content[card.current]
        if (!content) {
          continue
        }
        const options = content.options
        if (Array.isArray(options) && options.length > 0) {
          options.forEach((option: CardOption) => {
            if (
              option.type === CardOptionType.Card &&
              typeof option.link === 'number' &&
              !option.collapsed
            ) {
              const linkedCardId = this.$store.state.scripts.scheme.numsIds.get(
                option.link,
              )
              if (linkedCardId === undefined) {
                throw `Card with num ${option.link} not found.`
              }
              const nextCard = this.$store.state.scripts.scheme.map[
                linkedCardId
              ]
              if (!visited.has(option.link)) {
                queue.push(nextCard)
              }
            }
          })
        }
      }
      return nodes
    },
    filteredCards(): SchemeCardInfo[] {
      const search = this.searchValue.toLowerCase()
      return search ? this.shownCards.filter(card => {
        return card.name.toLowerCase().includes(search)
      }) : this.shownCards
    },
  },
  mounted() {
    this.updateScheme()
  }
})
